import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import {
    AixUsersService,
    isFeatureEnabled,
    Profile,
    PylonIdentity
} from '@trade-platform/ui-shared';
import { getFromStorage, setToStorage } from '@trade-platform/ui-utils';
import { firstValueFrom } from 'rxjs';
import { profileFeatures } from '../misc/constants';

export type PylonLoadStrategy = 'on-demand' | 'preload';

/**
 * THE load-timing switch. Everything else about the Pylon integration is identical in both modes.
 *
 * - `'on-demand'` (current): nothing is requested from Pylon until an eligible user clicks the
 *   support chat entrypoint. This is the agreed privacy posture — a fully user-initiated
 *   third-party load, comparable to the existing mailto support flow.
 * - `'preload'`: the widget script is injected anonymously once the profile is known, and identity
 *   is still only attached on click. Trades the click-to-open latency of the deferred path for
 *   earlier contact with Pylon.
 *
 * Flip this constant to switch; do not spread load-timing decisions anywhere else.
 *
 * NOTE: this switch governs the FIRST contact for a user who has never opened chat. It is orthogonal
 * to `resumeIfPreviouslyOpened` (trade-platform/trade-platform#10468), which — regardless of this
 * strategy — auto-boots and identifies on page load ONLY for a user who has already opened chat once
 * in this browser, so the native launcher and its unread-reply badge persist across refreshes.
 */
export const PYLON_LOAD_STRATEGY: PylonLoadStrategy = 'on-demand';

export const PYLON_SCRIPT_ID = 'pylon-chat-widget';
export const PYLON_WIDGET_ORIGIN = 'https://widget.usepylon.com';

/**
 * localStorage key prefix for the per-user "has ever opened chat here" flag. It is what makes the
 * persistent launcher (trade-platform/trade-platform#10468) consent-gated rather than a blanket
 * reversal of the on-demand posture: only once a user has opened chat at least once in this browser
 * does the widget auto-boot on subsequent page loads (so its native launcher and unread-reply badge
 * are persistently visible). A user who has never opened it — and every fresh browser or device —
 * still gets zero Pylon contact until their own first click on the fallback bubble.
 *
 * Keyed by the user's email so a different user signing in on a shared browser starts fresh with the
 * fallback bubble instead of silently auto-booting Pylon under the previous user's engagement.
 */
export const PYLON_OPENED_STORAGE_PREFIX = 'pylon-chat-opened:';

/**
 * Ceiling on repeat DETAIL VARIANCE captured per page — not a ceiling on captured aborts overall.
 *
 * It exists because a session provoking a new HTTP status on every click could otherwise emit an
 * event per status. It deliberately does NOT apply to the first sighting of a reason, since letting
 * noise crowd out an unseen diagnosis is the failure mode this telemetry exists to prevent.
 *
 * So the real per-page worst case is this value plus one first sighting for each of the OTHER
 * capturable reasons — the variants filling the ceiling already include the first sighting of the
 * reason they belong to, so it is not one extra per capturable reason. `PylonAbortReason` has 11
 * members and `feature-disabled` is never captured, leaving 10 capturable: 10 + 9 = 19 today.
 * Bounded, but not 10 — do not describe this constant as a total. Breadcrumbs are never capped.
 */
export const MAX_ABORT_VARIANTS_PER_PAGE = 10;

/**
 * How long `openChat` waits, after the widget bundle's `load` event, for the bundle to actually boot —
 * to replace our branded buffering `window.Pylon` stub with its own API. The bundle at `widget/<appId>`
 * is only a LOADER: it polls `window.pylon` config on a ~1s interval, then builds an iframe whose app
 * installs the real API a moment later, so the API is NOT present when the loader's script finishes
 * loading. Checking synchronously there always saw the stub and tore a healthy widget down — the
 * regression that shipped in advisor-ui .6.21 (trade-platform/trade-platform#10384). Measured boot with
 * valid identity in UAT was ~1.5s; this ceiling absorbs a slow network while still reporting a
 * genuinely dead entrypoint reasonably promptly.
 */
export const PYLON_BOOT_TIMEOUT_MS = 10000;

/** Poll interval while waiting up to `PYLON_BOOT_TIMEOUT_MS` for the widget to boot. */
export const PYLON_BOOT_POLL_MS = 100;

/**
 * Why every abort is reported to Sentry rather than merely breadcrumbed.
 *
 * This service is deliberately fail-closed: no path throws and none surfaces anything to the user.
 * That is right for the user, but it also meant the first UAT failure produced no telemetry at all —
 * `GET /me/pylon_identity` answered 200, the widget was never fetched, and the only way to find out
 * which guard had returned was to walk a tester through DevTools. Breadcrumbs alone would not have
 * closed that gap, because breadcrumbs are only transmitted attached to an event and nothing here
 * ever creates one. So an abort adds a breadcrumb AND captures a message — with one deliberate
 * exception, `feature-disabled`, which is breadcrumbed only, because the feature is off on most
 * tenants and capturing it would raise an event for every session platform-wide.
 *
 * One code per silent return, so a Sentry search identifies the guard without a reproduction.
 */
export type PylonAbortReason =
    /** Chat was already shut down for this page because a widget booted for a different user. */
    | 'blocked-until-reload'
    /** Tenant feature flag is off. Expected, not a fault — breadcrumbed but never captured. */
    | 'feature-disabled'
    /** A booted widget belongs to someone else and Pylon cannot be re-identified. */
    | 'user-changed-after-boot'
    /** advisors-api refused the identity request (403/503). */
    | 'identity-request-failed'
    /** A 200 whose body did not satisfy `pylonIdentityDecoder`. */
    | 'identity-decode-failed'
    /**
     * A decoded 200 whose required fields are missing OR empty. The guard is a truthiness check, so
     * `''` trips it exactly as an absent key does — the `has*` detail booleans mean "usable", not
     * "present".
     */
    | 'identity-incomplete'
    /** The signed-in user's profile email does not match the address the server hashed. */
    | 'email-mismatch-pre-boot'
    /** The signed-in user changed while the widget script was loading. */
    | 'email-mismatch-post-boot'
    /** A concurrent call shut chat down while this one was in flight. */
    | 'blocked-during-load'
    /** `widget.usepylon.com` was unreachable, blocked, or returned an error. */
    | 'script-load-failed'
    /**
     * The bundle loaded but did not boot within `PYLON_BOOT_TIMEOUT_MS`: `window.Pylon` is still the
     * branded buffering stub `installPylonQueueStub` seeded (or is absent) rather than the bundle's own
     * API — an unknown app id served a no-op, or an id collision with a foreign script.
     */
    | 'widget-api-missing';

/**
 * Email addresses are compared case-insensitively: the same person arriving as `User@Example.com`
 * from one source and `user@example.com` from another must not read as two different users.
 */
const normalizeEmail = (email: string | undefined) => email?.trim().toLowerCase();

/** The signed-in user's email, from the same cached profile the rest of the app reads. */
const currentUserEmail = () => normalizeEmail(getFromStorage<Profile>('profile')?.email);

/** Per-user localStorage key for the "has opened chat here before" flag; see the prefix constant. */
const openedStorageKey = (email: string | undefined) => `${PYLON_OPENED_STORAGE_PREFIX}${email}`;

/**
 * Pylon's widget API function. Pylon's documented install seeds this as a small queue stub BEFORE the
 * widget bundle loads: `q` buffers calls made while the bundle is still downloading, and the bundle
 * drains that queue and replaces the handler on boot. `installPylonQueueStub` creates it here.
 */
type PylonApi = ((command: string, ...args: unknown[]) => void) & {
    /** Calls buffered before the widget bundle finished loading; drained by the bundle on boot. */
    q?: unknown[];
    /** The buffering hook the bundle looks for; pushes one call's arguments onto `q`. */
    e?: (args: unknown[]) => void;
    /**
     * Present only on the buffering stub this service installs. The booted widget bundle replaces
     * `window.Pylon` with its own API, which does not carry this brand, so it is how the post-load
     * guard tells "the widget booted" from "the bundle loaded but left our stub in place".
     */
    __aixQueueStub?: true;
};

/** Pylon's documented widget globals. */
interface PylonWindow extends Window {
    pylon?: {
        chat_settings: {
            app_id: string;
            email: string;
            name: string;
            email_hash: string;
            account_external_id?: string;
        };
    };
    Pylon?: PylonApi;
}

@Injectable()
export class PylonChatService {
    private usersService = inject(AixUsersService);
    private gtag = inject(Angulartics2GoogleTagManager);

    /** Cached so concurrent clicks cannot inject the script twice. */
    private scriptLoad: Promise<void> | null = null;
    /**
     * The in-flight open attempt, cached so a second click that lands before the first settles joins it
     * instead of starting a duplicate. Necessary because booting the widget is asynchronous and can take
     * up to `PYLON_BOOT_TIMEOUT_MS`: without this, a click during that window would fire a second
     * identity request, a second boot poll, and — once booted — a second `show` and a second
     * `pylonChatOpened` metric (the reopen fast path cannot cover it, since `identifiedAs` is not set
     * until the boot completes). Cleared on settle so a later click always retries from scratch.
     */
    private opening: Promise<void> | null = null;
    /**
     * Whether the in-flight open should slide the chat PANEL out (and record engagement), as opposed to
     * just booting the launcher. Set by the user-initiated `openChat()` and read at the show step, so a
     * click during a page-load resume upgrades that shared attempt instead of starting a duplicate.
     * Reset when the attempt settles, alongside `opening`.
     */
    private wantsPanel = false;
    /** The email the loaded widget is identified as, or null when it is not identified. */
    private identifiedAs: string | null = null;
    /**
     * Set once a booted widget can no longer be trusted for the signed-in user. Removing the script
     * tag does not unload the Pylon runtime — `window.Pylon` survives it and stays bound to whoever
     * the widget was booted for — and Pylon documents no way to re-identify it. So the only sound
     * response is to stop offering chat for the rest of this page's life; a page load starts clean.
     */
    private blockedUntilReload = false;
    /**
     * Whether the Pylon widget has actually booted on this page (its own launcher is present). Exposed
     * as a signal so the persistent fallback bubble (`AixPylonLauncherComponent`) can hide itself the
     * moment the real Pylon launcher takes over, instead of stacking a second bubble bottom-right.
     * Set true on a successful boot; cleared by `teardown()` so a torn-down widget brings the fallback
     * bubble back for a retry.
     */
    readonly booted = signal(false);
    /**
     * Report fingerprints already captured for this page, so a repeatedly-clicked dead entrypoint
     * raises one Sentry event per DISTINCT diagnosis rather than one per click.
     *
     * Keyed on the reason PLUS its detail, not the reason alone. Two aborts sharing a reason can carry
     * materially different diagnoses — `identity-request-failed` with status 403 means a stale
     * feature/role cache, while 503 means Pylon is unconfigured — and suppressing the second would hide
     * the more useful one. Since this service otherwise fails silently, a breadcrumb without a captured
     * event may never be transmitted at all.
     *
     * Not a single page-wide flag either: most aborts leave chat retryable (they call `teardown()`, not
     * `blockUntilReload()`), so a later click can legitimately fail differently.
     */
    private readonly capturedFingerprints = new Set<string>();
    /**
     * Reasons captured at least once this page. Tracked separately from the fingerprints so the ceiling
     * can be applied only to repeat detail variance within a reason already seen, never to the first
     * sighting of a new one.
     */
    private readonly capturedReasons = new Set<PylonAbortReason>();

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    /**
     * Whether the support chat entrypoint should be offered at all. Tenant feature flag only — the
     * widget is available to every authenticated role on an enabled tenant.
     */
    isEnabled(): boolean {
        // Uses the local profileFeatures map rather than lib-enums' featureType: the published
        // @trade-platform/lib-enums does not carry `pylonChat` until the backend change releases.
        return isFeatureEnabled(profileFeatures.pylonChat);
    }

    /**
     * The single hook the nav bar calls once the profile is known. Runs the (optional) preload strategy
     * and then the consent-gated resume. Both are silent no-ops on the shipped `'on-demand'` path unless
     * the user has opened chat here before, so page load stays free of Pylon activity for everyone else.
     */
    async initForProfile(): Promise<void> {
        await this.preloadIfEnabled();
        await this.resumeIfPreviouslyOpened();
    }

    /**
     * Called once the profile is known. A no-op under `'on-demand'`, which is what keeps page load
     * free of any Pylon network activity.
     */
    async preloadIfEnabled(): Promise<void> {
        // Deliberately NOT reported: under the shipped 'on-demand' strategy this returns on every
        // profile load, so reporting here would emit an abort for every session on every tenant.
        if (PYLON_LOAD_STRATEGY !== 'preload' || !this.isEnabled()) {
            return;
        }
        const identity = await this.fetchIdentity();
        if (!identity) {
            return;
        }
        try {
            await this.injectScript(identity.appId);
        } catch {
            // Nothing is identified yet in this mode, but still reset so the click path can retry.
            this.abort('script-load-failed');
            this.teardown();
        }
    }

    /**
     * Auto-boot the widget on page load — WITHOUT opening the panel — for a user who has opened chat
     * here before, so Pylon's own launcher (and its unread-reply badge) is persistently visible and
     * survives a refresh (trade-platform/trade-platform#10468). Silent no-op when the feature is off or
     * the user has not opened chat here yet; those users keep the on-demand posture and see the
     * fallback bubble instead. A boot failure is reported and torn down exactly like `openChat`, which
     * brings the fallback bubble back so the user can retry.
     */
    async resumeIfPreviouslyOpened(): Promise<void> {
        if (!this.isEnabled() || !this.hasOpenedChatBefore()) {
            return;
        }
        // Through the shared in-flight guard, and WITHOUT setting `wantsPanel`, so this boots the
        // launcher but leaves the panel closed unless a click upgrades it.
        await this.open();
    }

    /** Whether this user has successfully opened chat in this browser before; see the storage prefix. */
    private hasOpenedChatBefore(): boolean {
        return getFromStorage<boolean>(openedStorageKey(currentUserEmail())) === true;
    }

    /** Record that this user has now opened chat here, so later page loads auto-boot the launcher. */
    private rememberOpenedChat(): void {
        setToStorage(openedStorageKey(currentUserEmail()), true);
    }

    /**
     * Fetch identity, boot the widget and open it. Safe to call repeatedly.
     *
     * Fails closed: if the feature is off, the request fails, or the server did not return both an
     * app id and an email hash, nothing is loaded and no identity is set. The support dropdown's
     * email and phone details remain as the fallback path.
     */
    openChat(): Promise<void> {
        // The user asked for the panel. Set this BEFORE joining any in-flight attempt so a click that
        // lands during a page-load resume (the fallback bubble is still shown until boot completes)
        // upgrades that same open to also slide the panel out, rather than starting a duplicate.
        this.wantsPanel = true;
        return this.open();
    }

    /** Shared in-flight guard for both entrypoints; see `opening`. */
    private open(): Promise<void> {
        this.opening ??= this.performOpen().finally(() => {
            this.opening = null;
            this.wantsPanel = false;
        });
        return this.opening;
    }

    /**
     * Boots and identifies the widget. Whether the chat PANEL is then slid open — and engagement
     * recorded — is governed by `wantsPanel`: a user-initiated `openChat()` sets it; the page-load
     * `resumeIfPreviouslyOpened()` does not, so it boots the launcher (and its badge) but leaves the
     * panel closed. Reading the field at the show step, rather than taking a parameter, is what lets a
     * click during an in-flight resume upgrade the shared attempt to open the panel.
     */
    private async performOpen(): Promise<void> {
        if (this.blockedUntilReload) {
            this.abort('blocked-until-reload');
            return;
        }
        if (!this.isEnabled()) {
            this.abort('feature-disabled');
            return;
        }

        const startedAt = Date.now();
        const pylonWindow = window as PylonWindow;

        if (this.identifiedAs && pylonWindow.Pylon) {
            if (this.identifiedAs !== currentUserEmail()) {
                this.abort('user-changed-after-boot', { hasProfileEmail: !!currentUserEmail() });
                this.blockUntilReload();
                return;
            }
            // Same user, already booted — reopen without re-measuring or re-fetching. Only slide the
            // panel out if this attempt actually wants it; a resume that finds the widget already up
            // has nothing to do (the launcher is already showing).
            if (this.wantsPanel) {
                pylonWindow.Pylon('show');
            }
            return;
        }

        const identity = await this.fetchIdentity();
        if (!identity) {
            // `fetchIdentity` has already reported the specific reason; reporting again here would
            // overwrite the precise cause with a vaguer one.
            return;
        }

        // The signed-in user may have changed while the request was in flight, or a concurrent call
        // may have decided chat must stay off. Re-check both after every await: the entry check above
        // cannot speak for state that changed while this call was suspended.
        if (this.blockedUntilReload) {
            this.abort('blocked-during-load');
            return;
        }
        if (normalizeEmail(identity.email) !== currentUserEmail()) {
            // The discriminator that cost a manual DevTools session: distinguishes "the cached profile
            // has no email at all" from "the two sources disagree on which address this user is".
            this.abort('email-mismatch-pre-boot', { hasProfileEmail: !!currentUserEmail() });
            return;
        }

        pylonWindow.pylon = {
            chat_settings: {
                app_id: identity.appId,
                // Must be exactly the address the server hashed, or Pylon rejects the hash.
                email: identity.email,
                name: identity.name,
                email_hash: identity.emailHash,
                ...(identity.tenantIdentifier
                    ? { account_external_id: identity.tenantIdentifier }
                    : {})
            }
        };

        // Pylon's widget is a two-part install: an inline `window.Pylon` queue stub, then the widget
        // bundle that drains the queue and takes the call handler over. The bundle assumes the stub
        // already exists — it does NOT create `window.Pylon` itself — so without this the API is never
        // installed, the `Pylon('show')` below is a silent no-op, and chat never opens
        // (trade-platform/trade-platform#10384, reported in Sentry as `widget-api-missing`). Seed the
        // documented stub, and only when absent so a widget booted by an earlier click is never
        // clobbered back to a buffering stub.
        this.installPylonQueueStub(pylonWindow);

        try {
            await this.injectScript(identity.appId);
        } catch {
            // Script blocked or unreachable. Leave the fallback support details in place.
            this.abort('script-load-failed');
            this.teardown();
            return;
        }

        // The bundle at `widget/<appId>` is only a LOADER: it polls `window.pylon` config on a ~1s
        // interval, then builds an iframe whose app installs the real API and replaces our branded
        // buffering stub. So the API is NOT present the instant the loader's `load` event fires —
        // checking there always sees the stub and tears a healthy widget down before it can boot. That
        // was the trade-platform/trade-platform#10384 regression that shipped in .6.21: the synchronous
        // stub check aborted every open, and `teardown()` then cleared the `window.pylon` config the
        // loader's interval still needed, so chat never opened for anyone. Wait for the boot instead.
        const booted = await this.awaitWidgetBoot(pylonWindow);
        // Read AFTER the wait: the boot replaces `window.Pylon` with a new API object, so a reference
        // captured earlier would still point at the stub.
        const pylon = pylonWindow.Pylon;

        // Still absent, or still our branded stub, after the boot ceiling: the bundle loaded but never
        // came up — an unknown app id served a no-op, or an id collision with a foreign script — so the
        // widget cannot work. Without this the seeded stub keeps `window.Pylon` truthy and later clicks
        // take the reopen fast path into a dead stub, so tear the state down and let a later click retry.
        if (!booted || !pylon) {
            this.abort('widget-api-missing');
            this.teardown();
            return;
        }

        // Booted. If the user changed during the load, this widget now belongs to the wrong person
        // and cannot be re-identified, so shut chat down for this page rather than showing it.
        if (normalizeEmail(identity.email) !== currentUserEmail()) {
            this.abort('email-mismatch-post-boot', { hasProfileEmail: !!currentUserEmail() });
            this.blockUntilReload();
            return;
        }

        // A concurrent call may have blocked chat while this one was loading. Never show the widget
        // past that decision — the block exists precisely because a booted widget cannot be trusted.
        if (this.blockedUntilReload) {
            this.abort('blocked-during-load');
            return;
        }

        this.identifiedAs = normalizeEmail(identity.email) ?? null;
        // The widget is up: its own launcher (and unread-reply badge) is now visible. Flip this so the
        // fallback bubble hides and hands off to the real launcher.
        this.booted.set(true);

        // Tenant routing key for Pylon's trigger rules, so a ticket lands in the right queue.
        if (identity.tenantIdentifier) {
            pylon('setNewIssueCustomFields', {
                tenant_identifier: identity.tenantIdentifier
            });
        }

        // A page-load resume boots the launcher but stops here — the panel stays closed and nothing is
        // re-recorded. Only a user-initiated open slides the panel out, records engagement so later page
        // loads auto-boot the launcher, and reports the click-to-open latency.
        if (!this.wantsPanel) {
            return;
        }

        pylon('show');
        this.rememberOpenedChat();

        // Click-to-open latency of the deferred path, so the decision to relax to `'preload'` can be
        // made with data. No PII — a duration and the load strategy only.
        this.gtag.eventTrack('pylonChatOpened', {
            category: 'support',
            label: PYLON_LOAD_STRATEGY,
            value: Date.now() - startedAt
        });
    }

    /**
     * Discards all Pylon state so a later click starts from scratch: clears the identity globals so
     * the user's email and hash are not left behind, drops the cached script promise, and removes the
     * script tag this service added.
     *
     * Only safe when no widget actually booted — see `blockUntilReload` for when one did.
     */
    private teardown(): void {
        const pylonWindow = window as PylonWindow;
        pylonWindow.pylon = undefined;
        this.identifiedAs = null;
        this.scriptLoad = null;
        // No widget is up any more, so the fallback bubble should reappear for a retry. (When
        // `blockUntilReload` calls teardown, the booted widget's own launcher may linger, but chat is
        // refused for the rest of the page regardless, and a reload starts clean.)
        this.booted.set(false);
        document.getElementById(PYLON_SCRIPT_ID)?.remove();
    }

    /**
     * Stops offering chat for the rest of this page's life.
     *
     * Used when a widget has already booted for a different user. Tearing down cannot undo that:
     * removing the script tag does not unload the Pylon runtime, `window.Pylon` stays registered and
     * bound to whoever it was booted for, and Pylon documents no way to re-identify it. Allowing a
     * retry would let the next click reopen that person's conversation history, so refuse instead.
     * Every sign-out in this app performs a page load, which clears all of it.
     */
    private blockUntilReload(): void {
        this.blockedUntilReload = true;
        this.teardown();
    }

    /**
     * Records why chat is not opening. Call this at every silent return; see `PylonAbortReason`.
     *
     * `detail` must contain shape only — booleans, counts, field NAMES. Never a value. `beforeSend`
     * (see `sentry.ts`) strips `user`, `extra` and `request.data`, but it does NOT touch message text,
     * tags or breadcrumbs, so anything passed here ships verbatim to Sentry. No email, no email hash,
     * no app id.
     */
    private abort(
        reason: PylonAbortReason,
        detail?: Record<string, boolean | number | string>
    ): void {
        Sentry.addBreadcrumb({ category: 'pylon', level: 'info', message: reason, data: detail });

        // The feature simply being off is the expected state on most tenants, not something to report.
        if (reason === 'feature-disabled') {
            return;
        }

        // Reason plus its detail, so 503-then-403 under one reason are both captured while an exact
        // repeat is not. Entries are sorted so key order cannot produce two fingerprints for one state.
        const fingerprint = [
            reason,
            ...Object.entries(detail ?? {})
                .map(([key, value]) => `${key}=${value}`)
                .sort()
        ].join('|');

        // An exact repeat is never worth a second event.
        if (this.capturedFingerprints.has(fingerprint)) {
            return;
        }

        // A reason not captured yet is ALWAYS captured, even once the ceiling is full. The ceiling
        // exists to stop noisy detail variance within a reason (a session provoking a new HTTP status
        // on every click) from flooding the project — but applying it globally let that same noise
        // crowd out the first sighting of a different, usually more diagnostic reason, which would only
        // be breadcrumbed and, since nothing here creates an event to carry it, effectively lost. That
        // is the exact blind spot this telemetry exists to close. `PylonAbortReason` is a closed union,
        // so first-sightings are inherently bounded and cannot themselves become a flood.
        if (
            this.capturedReasons.has(reason) &&
            this.capturedFingerprints.size >= MAX_ABORT_VARIANTS_PER_PAGE
        ) {
            return;
        }
        this.capturedFingerprints.add(fingerprint);
        this.capturedReasons.add(reason);

        // Assembled with an explicit loop rather than `Object.fromEntries`, which is ES2019 while this
        // library declares `lib: ["dom", "es2018"]` (tsconfig.lib.json). It does currently compile,
        // because a dependency raises the effective lib set through a transitive
        // `/// <reference lib="es2020" />`, but that is an accident of the dependency tree and not
        // something this file should rely on. `Object.entries` is ES2017 and inside the declared surface.
        const tags: Record<string, string> = { pylon_abort_reason: reason };
        for (const [key, value] of Object.entries(detail ?? {})) {
            tags[`pylon_${key}`] = String(value);
        }

        // Tags rather than `extra`: `extra` is deleted by `beforeSend`, and tags are indexed, so the
        // reason stays searchable without depending on breadcrumbs being rendered.
        Sentry.captureMessage(`Pylon chat unavailable: ${reason}`, { level: 'warning', tags });
    }

    private async fetchIdentity(): Promise<PylonIdentity | null> {
        let identity: PylonIdentity;
        try {
            identity = await firstValueFrom(this.usersService.getPylonIdentity());
        } catch (error) {
            // Two unrelated failures land here and used to be indistinguishable, which is exactly what
            // made the UAT incident hard: advisors-api refusing outright, versus a 200 whose body did
            // not satisfy `pylonIdentityDecoder`.
            if (error instanceof HttpErrorResponse) {
                this.abort('identity-request-failed', { status: error.status });
            } else {
                // Report only the failing key, never the decoder's message: ts.data.json embeds the
                // offending value in its text, which for this payload would put the user's email
                // address into Sentry. The character class keeps a value from arriving via the key.
                const failedKey = /at key "([A-Za-z0-9_]+)"/.exec(String(error))?.[1];
                this.abort('identity-decode-failed', failedKey ? { failedKey } : undefined);
            }
            return null;
        }

        // advisors-api answers 503 when Pylon is unconfigured, but guard the shape too rather
        // than building a widget URL out of an empty app id.
        //
        // Requiring the hash HERE rather than at the call site is what keeps the fail-closed
        // contract identical in both load strategies: Pylon works out who the chat user is from
        // the email the browser sets, so loading the widget without a hash covering that email
        // would let anyone read another user's conversations.
        //
        // `email` must be non-empty too: it is the value the widget identifies with, and an empty
        // one would compare equal to an empty profile email and slip past the user checks.
        if (!identity?.appId || !identity.emailHash || !identity.email) {
            this.abort('identity-incomplete', {
                hasAppId: !!identity?.appId,
                hasEmailHash: !!identity?.emailHash,
                hasEmail: !!identity?.email
            });
            return null;
        }
        return identity;
    }

    /**
     * Seeds Pylon's documented `window.Pylon` queue stub when nothing has installed the API yet. The
     * stub buffers commands in `q` until the widget bundle boots and drains them; see the call site in
     * `openChat` for why the bundle needs this rather than installing the API itself. Idempotent: a
     * widget already booted by an earlier click owns `window.Pylon` and must not be replaced.
     */
    private installPylonQueueStub(pylonWindow: PylonWindow): void {
        if (pylonWindow.Pylon) {
            return;
        }
        const queue: unknown[] = [];
        const enqueue = (args: unknown[]) => {
            queue.push(args);
        };
        const stub = ((...args: unknown[]) => enqueue(args)) as PylonApi;
        stub.q = queue;
        stub.e = enqueue;
        stub.__aixQueueStub = true;
        pylonWindow.Pylon = stub;
    }

    /**
     * Resolves `true` once the widget bundle has actually booted — it has replaced our branded buffering
     * stub on `window.Pylon` with its own API — or `false` if that has not happened within
     * `PYLON_BOOT_TIMEOUT_MS`. The boot is asynchronous and cannot be observed when `injectScript`
     * resolves: the `widget/<appId>` bundle is only a loader that polls `window.pylon` config on a ~1s
     * interval, then builds an iframe whose app installs the API a moment later. See
     * `PYLON_BOOT_TIMEOUT_MS` for why this poll replaced the synchronous check that broke #10384.
     */
    private awaitWidgetBoot(pylonWindow: PylonWindow): Promise<boolean> {
        const hasBooted = () => !!pylonWindow.Pylon && !pylonWindow.Pylon.__aixQueueStub;
        if (hasBooted()) {
            return Promise.resolve(true);
        }
        return new Promise<boolean>(resolve => {
            const startedAt = Date.now();
            const timer = window.setInterval(() => {
                if (hasBooted()) {
                    window.clearInterval(timer);
                    resolve(true);
                } else if (Date.now() - startedAt >= PYLON_BOOT_TIMEOUT_MS) {
                    window.clearInterval(timer);
                    resolve(false);
                }
            }, PYLON_BOOT_POLL_MS);
        });
    }

    private injectScript(appId: string): Promise<void> {
        if (this.scriptLoad) {
            return this.scriptLoad;
        }

        this.scriptLoad = new Promise<void>((resolve, reject) => {
            const existing = document.getElementById(PYLON_SCRIPT_ID);
            if (existing) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.id = PYLON_SCRIPT_ID;
            script.src = `${PYLON_WIDGET_ORIGIN}/widget/${encodeURIComponent(appId)}`;
            script.async = true;
            script.addEventListener('load', () => resolve());
            script.addEventListener('error', () =>
                reject(new Error('Pylon widget failed to load'))
            );
            document.head.appendChild(script);
        }).catch(err => {
            // Allow a later click to retry a transient failure.
            this.scriptLoad = null;
            document.getElementById(PYLON_SCRIPT_ID)?.remove();
            throw err;
        });

        return this.scriptLoad;
    }
}
